/**
 * SessionLiveness — JAISIU-3337
 *
 * Reconcile the three independent "is this session alive?" signals:
 *
 *   A. Floci heartbeat  → consumer (gateway container liveness)
 *   B. chat.run         → consumer (current tab's chat.send stream)
 *   C. subagent run     → consumer (fanned-out sessions.spawn children)
 *   D. background runs  → consumer (inject / announce finals in flight)
 *
 * Each producer has its own latency and drift surface. Before this
 * module the chat row and the agent roster computed their own answers
 * and disagreed on three concrete scenarios:
 *
 *   1. Fleet-question pending for the session  → row says idle
 *      while a `FleetQuestionRecord` is awaiting operator input.
 *   2. Subagent finished but parent still answering → roster
 *      reports `activeRuns = N` even after a subagent transitions
 *      through `running → done` without removal from `chatSubagents`.
 *   3. Floci heartbeat just expired (30s gap) → roster says
 *      inactive but the row still claims a run is in flight.
 *
 * `computeSessionLiveness` folds all three producers into one
 * `SessionLiveness` value that both consumers (chat row + agent
 * roster) read from. Pure function, no side effects, no event
 * subscription — the caller wires it into the existing render path.
 */

import type { ChatSubagentCard } from "../ui-types.ts";
import { isSubagentRunActive } from "./subagent-run-status.ts";

/**
 * JAISIU-types-sweep: default heartbeat TTL for `computeSessionLiveness`.
 * 5 minutes matches the in-memory LivenessMonitor threshold in
 * `gateway/stuck-session-monitor.ts`; sessions with no recorded
 * last-event timestamp or age beyond this are considered stale.
 */
export const HEARTBEAT_TTL_MS_DEFAULT = 5 * 60_000;

/**
 * Why the session is currently live. The values are ordered by
 * rendering preference (most actionable first) so consumers can pick
 * `liveReason` for chips without re-sorting.
 */
export type LiveReason = "chat-run" | "fleet-question" | "subagent" | "compaction" | null;

export type SessionLiveness = {
  /**
   * True when *any* producer says the session is doing something
   * right now. The single boolean the chat row's run-active
   * derivation should consume (was: three booleans OR'd in
   * `chat.ts:1444`).
   */
  isLive: boolean;

  /**
   * Why `isLive`. Useful for chips ("live · fleet question") and
   * for the live-run step strip's `label`. `null` when `!isLive`.
   */
  liveReason: LiveReason;

  /**
   * Subagent cards that are still actually active, with the stale
   * `done`/`error` cards filtered out. The agent roster should
   * consume this in place of the raw `chatSubagents` array so it
   * never reports ghost runs.
   */
  activeSubagents: ChatSubagentCard[];

  /**
   * Count of active subagent runs after the same filter. Replaces
   * the buggy `existing.activeRuns + 1` accumulator in
   * `agent-roster-model.ts` (which never decremented).
   */
  activeRunCount: number;

  /**
   * True when the row's last observed event is older than
   * `heartbeatTtlMs`. Signals to the renderer that the run state
   * may be stale even when `isLive` is true — a separate, more
   * cautious chip than "running".
   */
  gatewayStale: boolean;
};

/**
 * Fold the three producers into one liveness value.
 *
 * Inputs are passed in the same shape the existing render sites
 * already have. No new event types, no new RPCs, no gateway
 * changes — this is a UI-side reconciliation only.
 *
 * @param params.runActive        chat.send is currently in flight
 *                                for this session tab (chat.ts:1444
 *                                derivation). Pure UI signal.
 * @param params.compactionActive SessionSnapshot reports compaction
 *                                in progress. Optional — pass
 *                                `false` if not exposed.
 * @param params.fleetQuestion    A `FleetQuestionRequest` is
 *                                pending for this session key.
 *                                Optional — `null` means "no
 *                                pending question".
 * @param params.subagents        Raw `chatSubagent` array for this
 *                                session key, including done/error.
 *                                The function filters internally.
 * @param params.lastEventAtMs    Wall-clock timestamp of the most
 *                                recent chat event for this row.
 *                                Used for the staleness check.
 * @param params.nowMs            Caller-supplied "now" so renders
 *                                stay deterministic in tests.
 * @param params.heartbeatTtlMs   Threshold above which the row is
 *                                considered stale. Should match the
 *                                Floci heartbeat TTL (30s by
 *                                default, per `floci-crowd.md`).
 */
export function computeSessionLiveness(params: {
  runActive: boolean;
  backgroundRunsActive?: boolean;
  compactionActive?: boolean;
  fleetQuestion?: { sessionKey: string } | null;
  subagents?: ChatSubagentCard[];
  /**
   * JAISIU-types-sweep: timestamp of the session's last activity, or
   * `null` for cold-start sessions that haven't recorded one yet.
   * Optional so the chat-history sidebar's badge tooling can omit
   * the field for never-active sessions; the load path coalesces
   * missing values to `null` so a stale-event check still returns
   * `false` (cold start is not stale).
   */
  lastEventAtMs?: number | null;
  /**
   * JAISIU-types-sweep: optional on the type — callers that compute
   * staleness outside the chat-history sidebar (e.g. preview cards,
   * badge tooling, debug overlays) coalesce to `Date.now()` here so
   * they don't have to thread a value through.
   */
  nowMs?: number;
  heartbeatTtlMs?: number;
}): SessionLiveness {
  const {
    runActive,
    backgroundRunsActive = false,
    compactionActive = false,
    fleetQuestion = null,
    subagents = [],
    lastEventAtMs = null,
    nowMs = Date.now(),
    heartbeatTtlMs = HEARTBEAT_TTL_MS_DEFAULT,
  } = params;

  // Filter subagents once. Both `activeSubagents` and
  // `activeRunCount` are derived from this so the two values can
  // never disagree.
  const activeSubagents = subagents.filter((card) => isSubagentRunActive(card.status));
  const activeRunCount = activeSubagents.length;

  // The session is live if *any* of these hold. Order matters for
  // `liveReason` only — we pick the most actionable label, not the
  // first one that happened to be true.
  const hasFleetQuestion = fleetQuestion != null && fleetQuestion.sessionKey.trim().length > 0;
  const subagentLive = activeRunCount > 0;

  // Pick the reason label. `chat-run` wins over `fleet-question`
  // when both are true (the run is the more actionable thing to
  // surface), and `fleet-question` wins over `subagent` (the
  // operator is the blocker, not a subagent).
  let liveReason: LiveReason = null;
  if (runActive) {
    liveReason = "chat-run";
  } else if (compactionActive) {
    liveReason = "compaction";
  } else if (hasFleetQuestion) {
    liveReason = "fleet-question";
  } else if (subagentLive || backgroundRunsActive) {
    liveReason = "subagent";
  }

  const isLive =
    runActive || backgroundRunsActive || compactionActive || hasFleetQuestion || subagentLive;

  // Staleness: only meaningful when we have a reference timestamp.
  // `null` means the caller has never seen an event for this row
  // (cold start, fresh session) — treat as not-stale rather than
  // as "infinitely stale", which would false-positive every reload.
  const gatewayStale = lastEventAtMs != null && nowMs - lastEventAtMs > heartbeatTtlMs;

  return {
    isLive,
    liveReason,
    activeSubagents,
    activeRunCount,
    gatewayStale,
  };
}
